import { Alert, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Stack, TextField } from '@mui/material';
import * as React from 'react';

import { DeveloperApiError, useDeveloperApi } from './api';
import { describeError, errorEntries } from './components';
import type { SandboxAdmin } from './sandboxTypes';
import { SigninError } from './SigninError';

/** The User model's name length (the backend refuses longer names with `name_required`). */
const NAME_MAX = 150;

/** The refusal's lines: a weak password lists the rules it breaks; other refusals their `errors` entries. */
const reasons = (error: unknown): string[] => {
    if (error instanceof DeveloperApiError && error.code === 'password_weak') {
        return error.errors.map((entry) => entry.error || entry.message).filter(Boolean);
    }
    return errorEntries(error);
};

const Refusal = ({ error }: { error: unknown }) => {
    if (!error) {
        return null;
    }
    if (error instanceof DeveloperApiError && error.code === 'mfa_required') {
        return <SigninError error={error} />;
    }
    const lines = reasons(error);
    return (
        <Alert severity="error" data-testid="sandbox-admin-error">
            {describeError(error)}
            {lines.length > 0 && (
                <Box component="ul" sx={{ m: 0, pl: 2 }}>
                    {lines.map((line) => <li key={line}>{line}</li>)}
                </Box>
            )}
        </Alert>
    );
};

interface Props {
    open: boolean;
    onClose: () => void;
    /** Called once the admin exists (the Overview is read again: "Open sandbox" then signs in as them). */
    onAdded: (admin: SandboxAdmin) => void;
    sandboxName: string;
}

/** "Add sandbox admin" (DSEC D6; developer.openapi.yaml developerSandboxAdminAdd): the first admin of a sandbox
 *  created without one, so "Open sandbox" has somebody to sign in as. The password is optional and kept only in
 *  this dialog's state while it is open: closing it drops every field. */
const AddSandboxAdminDialog = ({ open, onClose, onAdded, sandboxName }: Props) => {
    const api = useDeveloperApi();
    const [email, setEmail] = React.useState('');
    const [firstName, setFirstName] = React.useState('');
    const [lastName, setLastName] = React.useState('');
    const [password, setPassword] = React.useState('');
    const [saving, setSaving] = React.useState(false);
    const [error, setError] = React.useState<unknown>(null);

    // Once the dialog has faded out, the next one starts empty: the password is gone.
    const reset = () => {
        setEmail('');
        setFirstName('');
        setLastName('');
        setPassword('');
        setSaving(false);
        setError(null);
    };

    const ready = !!email.trim() && !!firstName.trim() && !!lastName.trim() && !saving;
    const code = error instanceof DeveloperApiError ? error.code : null;

    const add = async () => {
        setSaving(true);
        setError(null);
        try {
            const added = await api.addSandboxAdmin({
                email: email.trim(),
                first_name: firstName.trim(),
                last_name: lastName.trim(),
                ...(password ? { password } : {}),
            });
            setPassword('');
            onAdded(added.admin);
        } catch (e) {
            setError(e);
        } finally {
            setSaving(false);
        }
    };

    const field = (props: React.ComponentProps<typeof TextField>) => (
        <TextField size="small" fullWidth disabled={saving} {...props} />
    );

    return (
        <Dialog
            open={open}
            // While the admin is being added nothing closes it, so the answer cannot land in a closed dialog.
            onClose={saving ? undefined : onClose}
            fullWidth
            maxWidth="xs"
            aria-labelledby="add-sandbox-admin-title"
            TransitionProps={{ onExited: reset }}
            // Above the app's side menu (zIndex 10000 in layout/main.js).
            sx={{ zIndex: 10002 }}
        >
            <DialogTitle id="add-sandbox-admin-title">Add sandbox admin</DialogTitle>
            <DialogContent>
                <DialogContentText sx={{ mb: 2 }}>
                    {sandboxName} has no administrator, so there is nobody for “Open sandbox” to sign in as. Add one: a
                    new user of the sandbox, separate from production’s accounts.
                </DialogContentText>
                <Stack spacing={2}>
                    {field({
                        autoFocus: true,
                        required: true,
                        type: 'email',
                        label: 'E-mail',
                        value: email,
                        onChange: (ev) => setEmail(ev.target.value),
                        error: code === 'email_taken' || code === 'email_invalid',
                        helperText: 'The admin’s login. No user of any company may have it already.',
                        inputProps: { maxLength: 254, autoComplete: 'off' },
                    })}
                    <Stack direction="row" spacing={2}>
                        {field({
                            required: true,
                            label: 'First name',
                            value: firstName,
                            onChange: (ev) => setFirstName(ev.target.value),
                            error: code === 'name_required',
                            inputProps: { maxLength: NAME_MAX },
                        })}
                        {field({
                            required: true,
                            label: 'Last name',
                            value: lastName,
                            onChange: (ev) => setLastName(ev.target.value),
                            error: code === 'name_required',
                            inputProps: { maxLength: NAME_MAX },
                        })}
                    </Stack>
                    {field({
                        type: 'password',
                        label: 'Password (optional)',
                        value: password,
                        onChange: (ev) => setPassword(ev.target.value),
                        error: code === 'password_weak',
                        helperText: 'Without a password the admin signs in from production only, through “Open sandbox”. '
                            + 'With one, they can also sign in on the sandbox’s own dashboard.',
                        inputProps: { autoComplete: 'new-password' },
                    })}
                    <Refusal error={error} />
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={saving}>Cancel</Button>
                <Button
                    variant="contained"
                    onClick={add}
                    disabled={!ready}
                    startIcon={saving ? <CircularProgress size={14} /> : undefined}
                >
                    Add admin
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default AddSandboxAdminDialog;
